import { useState, type FC } from "react";
import { ArrowRight, Calendar, Check, Lock, Upload } from "lucide-react";
import { format } from "date-fns";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { cn } from "@/shared/utils/core";
import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { UploadDocumentModal } from "@/shared/components/overlay/UploadDocumentModal";
import { useUserCustomizations } from "@/shared/hooks/useUserCustomizations";
import { useCalendlyBooking } from "@/shared/hooks/useCalendlyBooking";
import { useHasAuthorityDocument } from "@/shared/hooks/useHasAuthorityDocument";

import { supabase } from "@/shared/lib/supabase";
import { hasMinTier } from "@/shared/utils/tierOrder";
import { getAuthorityDocumentType } from "@/shared/constants/legalAuthorityDocuments";
import {
  isAssetSearchComplete,
  isAssetSearchInProgress,
  isAssetSearchSubmitted,
} from "@/shared/constants/assetSearch";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { ESTATE_EXPERT_BOOKING_SOURCE } from "@/shared/constants/calendlyUrls";

import type { PaymentTier } from "@/shared/utils/tierOrder";

import { WhatWeKnowSidebar } from "./WhatWeKnowSidebar";

import overlayImg from "@/shared/assets/get-authority/generic-paywall-overlay.png";

const AUTHORITY_PATH_LABELS: Record<string, string> = {
  probate: "probate",
  administration: "administration",
  sea: "a small estate affidavit",
};

interface GenericAuthorityGuideProps {
  stateName: string;
  countyLabel: string;
  decedentFirstName: string;
  authorityDocumentName: string;
  authorityPath: string | null;
  isPaidUser: boolean;
  tier: PaymentTier | null;
  estateId: number;
  onOpenPaywall: () => void;
  onOpenCalendly: () => void;
  setCalendlyOpen: (open: boolean) => void;
  familyCount: number;
  propertyCount: number;
  willStatus: string | null;
  keyDocCount: number;
  keyDocTotal: number;
  attorneyName: string | null;
  stateCode: string | null;
  hasEstateAccount?: boolean;
  hasAuthorityDocument?: boolean;
  assetSearchStatus?: string;
  hasWill?: string | null;
}

export const GenericAuthorityGuide: FC<GenericAuthorityGuideProps> = ({
  stateName,
  countyLabel,
  decedentFirstName,
  authorityDocumentName,
  authorityPath,
  isPaidUser,
  tier,
  estateId,
  onOpenPaywall,
  onOpenCalendly,
  setCalendlyOpen,
  familyCount,
  propertyCount,
  willStatus,
  keyDocCount,
  keyDocTotal,
  attorneyName,
  stateCode,
  hasEstateAccount = false,
  hasAuthorityDocument = false,
  assetSearchStatus,
  hasWill,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const { getCustomization, setCustomization } = useUserCustomizations();
  const { booking } = useCalendlyBooking(isPaidUser ? estateId : undefined, ESTATE_EXPERT_BOOKING_SOURCE);
  const { setHasAuthorityDocument } = useHasAuthorityDocument(estateId);
  const endTimePassed = booking?.end_time ? new Date(booking.end_time) < new Date() : false;
  const staleBookingFallback =
    !endTimePassed &&
    booking?.event_status === "unavailable" &&
    !!booking.booked_at &&
    Date.now() - new Date(booking.booked_at).getTime() > 48 * 60 * 60 * 1000;
  const callCompleted = endTimePassed || staleBookingFallback;
  const isBasicOnly = hasMinTier(tier, "basic") && !hasMinTier(tier, "premium");

  const referralRequestedAt = getCustomization<string>(
    `attorney_referral_requested_${estateId}`,
    null,
  );
  const hasRequestedReferral = !!referralRequestedAt;

  const handleBookCall = () => {
    if (isPaidUser) {
      onOpenCalendly();
    } else {
      onOpenPaywall();
    }
  };

  const handleRequestReferral = async () => {
    setIsRequesting(true);
    try {
      const { error } = await supabase.functions.invoke("communications/slack-notify", {
        body: {
          event: "attorney_referral_requested",
          data: {
            estate_id: estateId,
            state: stateCode,
            county: countyLabel.replace(/ County$/i, ""),
          },
        },
      });
      if (error) throw error;
      await setCustomization(`attorney_referral_requested_${estateId}`, new Date().toISOString());
      await queryClient.invalidateQueries({ queryKey: ["userData"] });
      enqueueSnackbar("Referral requested. We'll introduce you to an attorney soon.", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
    } finally {
      setIsRequesting(false);
    }
  };

  const handleUploadLetters = () => {
    if (isPaidUser) {
      setUploadOpen(true);
    } else {
      onOpenPaywall();
    }
  };

  const stepOffset = 1;

  const header = (
    <div className="flex flex-col gap-4">
      <div className="flex">
        <Tag color="white" icon={Check}>
          Your results
        </Tag>
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="text-xl font-semibold leading-[30px]">
          {decedentFirstName}&apos;s estate goes through{" "}
          {authorityPath
            ? (AUTHORITY_PATH_LABELS[authorityPath] ?? "court appointment")
            : "court appointment"}
        </h3>
        <p className="text-sm text-secondary">
          {authorityPath === "probate"
            ? `The will names you, but the court is what puts you in charge. The proof is your legal authority, the court paper that lets you act for ${decedentFirstName}. Until the ${countyLabel} court gives you yours, you can't open an estate bank account, sell the car, or move money. These steps get you that paper.`
            : `The court is what puts you in charge. The proof is your legal authority, the court paper that lets you act for ${decedentFirstName}. Until the ${countyLabel} court gives you yours, you can't open an estate bank account, sell the car, or move money. These steps get you that paper.`}
        </p>
      </div>
    </div>
  );

  const sidebar = (
    <WhatWeKnowSidebar
      familyCount={familyCount}
      propertyCount={propertyCount}
      willStatus={willStatus}
      keyDocCount={keyDocCount}
      keyDocTotal={keyDocTotal}
      attorneyName={attorneyName}
      countyLabel={countyLabel}
      stateCode={stateCode}
      decedentFirstName={decedentFirstName}
      expertName={null}
    />
  );

  if (!isPaidUser) {
    return (
      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="flex flex-1 flex-col gap-8">
          {header}

          <hr className="border-primary" />

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold">Your path to get appointed</p>
              <p className="text-sm text-secondary">
                Start with your Estate Expert. They&apos;ll tell you which of the next steps fit{" "}
                {decedentFirstName}&apos;s estate.
              </p>
            </div>

            <div className="relative overflow-hidden rounded-lg">
              <img src={overlayImg} alt="" aria-hidden="true" className="hidden w-full sm:block" />

              <div className="relative sm:absolute sm:left-1/2 sm:top-1/2 sm:w-[500px] sm:max-w-[calc(100%-16px)] sm:-translate-x-1/2 sm:-translate-y-1/2 rounded-lg border border-brand bg-white p-6 flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-center rounded-full bg-brand-subtle p-4 w-fit">
                    <Lock className="h-6 w-6 text-brand" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-xl font-semibold leading-[30px]">
                      Get the court&apos;s permission to act for {decedentFirstName}
                    </p>
                    <p className="text-sm text-secondary">
                      {authorityPath === "probate"
                        ? `${decedentFirstName}'s will names you, but a will alone proves nothing to a bank. Only ${/^[aeiou]/i.test(stateName) ? "an" : "a"} ${stateName} court can give you the power to act, by appointing you. This step gets you to that appointment.`
                        : `Only ${/^[aeiou]/i.test(stateName) ? "an" : "a"} ${stateName} court can give you the power to act for ${decedentFirstName}'s estate, by appointing you. This step gets you to that appointment.`}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5 shrink-0 text-brand" />
                      <p className="text-sm">
                        A 30-minute call with your Estate Expert, booked right after you pay
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5 shrink-0 text-brand" />
                      <p className="text-sm">
                        Matches with {stateName} estate attorneys, if you want one
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5 shrink-0 text-brand" />
                      <p className="text-sm">
                        Room to invite any attorney you&apos;re working with
                      </p>
                    </div>
                  </div>
                </div>

                <hr className="border-primary" />

                <div className="flex items-center gap-6">
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-semibold leading-[30px]">$500</span>
                      <span className="text-sm text-secondary">one time</span>
                    </div>
                    <p className="text-sm font-medium text-brand">An eligible estate expense</p>
                  </div>
                  <Button variant="brand" rounded onClick={onOpenPaywall}>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>

                <p className="text-xs text-secondary text-center">
                  {hasEstateAccount
                    ? "This is an eligible estate expense. It also counts toward Elayne Asset Search or Estate Settlement if you add them later."
                    : "There's no estate bank account yet, so this goes on your card. Once the court appoints you, the estate pays you back, like expensing a cost at work. Add Elayne Asset Search or Estate Settlement later and this $500 counts toward it. $6,000 is the most any estate pays."}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden w-[300px] shrink-0 lg:block">{sidebar}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 lg:flex-row">
      <div className="flex flex-1 flex-col gap-8">
        {header}

        <hr className="border-primary" />

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-base font-semibold">Your path to get appointed</p>
            <p className="text-sm text-secondary">
              Start with your Estate Expert. They&apos;ll tell you which of the next steps fit{" "}
              {decedentFirstName}&apos;s estate.
            </p>
          </div>

          <div className="overflow-hidden rounded-lg border border-primary">
            {callCompleted ? (
              <div className="flex gap-4 bg-white p-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold text-tertiary">Talk to your Estate Expert</p>
                  <p className="text-sm text-tertiary">
                    Talked with your Estate Expert on{" "}
                    {booking?.start_time ? format(new Date(booking.start_time), "MMMM d") : ""}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex gap-4 rounded-t-lg border border-brand bg-brand-subtle p-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-medium leading-none text-white">
                  1
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  {booking ? (
                    <>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold">Talk to your Estate Expert</p>
                          <Tag color="green" icon={Check}>
                            Booked
                          </Tag>
                        </div>
                        <p className="text-sm text-secondary">
                          You&apos;re all set. Bring your questions about {decedentFirstName}&apos;s
                          estate.
                        </p>
                      </div>
                      <div className="flex items-center gap-4 rounded-lg border border-primary bg-white p-4">
                        <Calendar className="h-5 w-5 shrink-0 text-secondary" />
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          <p className="text-sm font-medium">
                            {booking.start_time
                              ? format(
                                  new Date(booking.start_time),
                                  "EEEE, MMMM d 'at' h:mm a 'ET'",
                                )
                              : "Call scheduled"}
                          </p>
                          <p className="text-xs text-secondary">
                            {booking.event_name ?? "30 minutes with your Estate Expert"}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCalendlyOpen(true)}
                          className="shrink-0 text-sm font-medium text-secondary underline"
                        >
                          Reschedule
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-semibold">Talk to your Estate Expert</p>
                        <p className="text-sm text-secondary">
                          Get matched for a 30-minute, one-on-one call with an Estate Expert who has
                          guided hundreds of families through settlement. They'll review everything
                          you've added about the estate with you, answer your questions, and you'll
                          leave knowing what comes next.
                        </p>
                      </div>
                      <div>
                        <Button variant="brand" size="md" onClick={handleBookCall}>
                          Book call
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {hasRequestedReferral || attorneyName ? (
              <div className="flex gap-4 bg-white p-4 border-b border-primary">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold text-tertiary">
                    Request an attorney referral
                  </p>
                  <p className="text-sm text-tertiary">
                    We'll introduce you to a probate attorney near {countyLabel}. They're
                    independent from Elayne, and we don't take referral fees.
                  </p>
                </div>
              </div>
            ) : (
              <div
                className={cn(
                  "flex gap-4 p-4",
                  callCompleted
                    ? "border border-brand bg-brand-subtle"
                    : "border-b border-primary bg-white",
                )}
              >
                <div
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium leading-none",
                    callCompleted ? "bg-brand text-white" : "border border-primary bg-white",
                  )}
                >
                  2
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">Request an attorney referral</p>
                      {callCompleted && (
                        <Tag color="white" size="sm" className="text-brand">
                          You're here
                        </Tag>
                      )}
                    </div>
                    <p className="text-sm text-secondary">
                      {`If you'd like an attorney to file for you, we'll introduce you to a probate attorney in ${countyLabel}. They're independent from us, and we don't take referral fees.`}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    {callCompleted ? (
                      <Button
                        variant="brand"
                        size="md"
                        onClick={handleRequestReferral}
                        disabled={isRequesting}
                      >
                        {isRequesting ? "Requesting..." : "Request referral"}
                      </Button>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                variant="outline"
                                size="md"
                                onClick={handleRequestReferral}
                                disabled={true}
                              >
                                Request referral
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Request a referral after your call with your Estate Expert. They'll look
                            at {decedentFirstName}&apos;s estate with you first, since not every
                            estate needs an attorney. If yours does, this unlocks right after the
                            call.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <a
                      href={`${POST_LOSS_ROUTES.CONTACTS}?add=attorney`}
                      className="text-sm font-medium underline"
                    >
                      Or add your attorney
                    </a>
                  </div>
                </div>
              </div>
            )}

            {(() => {
              const step2Done = hasRequestedReferral || !!attorneyName;
              const isCurrentStep = callCompleted && step2Done && !hasAuthorityDocument;
              return (
                <div
                  className={cn(
                    "flex gap-4 p-4",
                    hasAuthorityDocument
                      ? "border-b border-primary bg-white"
                      : isCurrentStep
                        ? "border border-brand bg-brand-subtle"
                        : "border-b border-primary bg-white",
                  )}
                >
                  {hasAuthorityDocument ? (
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  ) : (
                    <div
                      className={cn(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium leading-none",
                        isCurrentStep ? "bg-brand text-white" : "border border-primary bg-white",
                      )}
                    >
                      {2 + stepOffset}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <p
                        className={cn(
                          "text-sm font-semibold",
                          hasAuthorityDocument && "text-tertiary",
                        )}
                      >
                        Get appointed by the {countyLabel} court
                      </p>
                      {isCurrentStep && (
                        <Tag color="white" size="sm" className="text-brand">
                          You're here
                        </Tag>
                      )}
                    </div>
                    <p
                      className={cn(
                        "text-sm text-secondary",
                        hasAuthorityDocument && "text-tertiary",
                      )}
                    >
                      {`You or your attorney file a petition, the form that asks the court to put you in charge. When the court approves it, you're appointed and get your legal authority, a court paper called ${authorityDocumentName}.`}
                    </p>
                  </div>
                </div>
              );
            })()}

            {hasAuthorityDocument ? (
              <div
                className={`flex gap-4 bg-white p-4${isBasicOnly ? " border-b border-primary" : ""}`}
              >
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold text-tertiary">
                    Share your {authorityDocumentName}
                  </p>
                  <p className="text-sm text-tertiary">
                    {`Add them here once the court sends them. Steps that need your legal authority, like the search for ${decedentFirstName}'s accounts, start once they're here.`}
                  </p>
                </div>
              </div>
            ) : (
              <div
                className={`flex gap-4 bg-white p-4${isBasicOnly ? " border-b border-primary" : ""}`}
              >
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-white text-sm font-medium leading-none">
                  {3 + stepOffset}
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold">Share your {authorityDocumentName}</p>
                    <p className="text-sm text-secondary">
                      {`Add them here once the court sends them. Steps that need your legal authority, like the search for ${decedentFirstName}'s accounts, start once they're here.`}
                    </p>
                  </div>
                  <div>
                    <Button variant="outline" size="md" onClick={handleUploadLetters}>
                      <Upload className="h-4 w-4" />
                      Upload letters
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {isAssetSearchComplete(assetSearchStatus) ? (
              <div className="flex gap-4 bg-white p-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold text-tertiary">Elayne Asset Search</p>
                  <p className="text-xs text-tertiary">Completed</p>
                </div>
              </div>
            ) : isAssetSearchInProgress(assetSearchStatus) ? (
              <div className="flex gap-4 bg-white p-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-white text-sm font-medium leading-none">
                  {4 + stepOffset}
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">Elayne Asset Search</p>
                      <Tag color="green">In progress</Tag>
                    </div>
                    <p className="text-sm text-secondary">
                      Looks for retirement accounts, bank accounts, life insurance, and debts across
                      100+ sources. Your legal authority is on file, so results show up here as
                      they&apos;re found.
                    </p>
                  </div>
                  <div>
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => navigate(POST_LOSS_ROUTES.FIND_ASSETS)}
                    >
                      View search
                    </Button>
                  </div>
                </div>
              </div>
            ) : hasMinTier(tier, "premium") &&
              !hasAuthorityDocument &&
              !isAssetSearchSubmitted(assetSearchStatus) ? (
              <div className="flex gap-4 bg-white p-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-white text-sm font-medium leading-none">
                  {4 + stepOffset}
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">Elayne Asset Search</p>
                      <Tag color="green">Purchased</Tag>
                    </div>
                    <p className="text-sm text-secondary">
                      {`Real estate in ${decedentFirstName}'s name and money turned over to the state are being searched now. Retirement accounts, bank accounts, life insurance, and debts start when you share your legal authority.`}
                    </p>
                  </div>
                </div>
              </div>
            ) : hasAuthorityDocument && !hasMinTier(tier, "premium") ? (
              <div className="flex gap-4 bg-white p-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-white text-sm font-medium leading-none">
                  {4 + stepOffset}
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold">Elayne Asset Search</p>
                    <p className="text-sm text-secondary">
                      Looks for retirement accounts, bank accounts, life insurance, and debts across
                      100+ sources. It runs once your legal authority is added.
                    </p>
                  </div>
                  <div>
                    <Button
                      variant="brand"
                      size="md"
                      onClick={() => navigate(POST_LOSS_ROUTES.FIND_ASSETS)}
                    >
                      Unlock your search
                    </Button>
                  </div>
                </div>
              </div>
            ) : isBasicOnly ? (
              <div className="flex gap-4 bg-white p-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-white text-sm font-medium leading-none">
                  {4 + stepOffset}
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">Elayne Asset Search</p>
                      <Tag color="green">Your $500 counts toward it</Tag>
                    </div>
                    <p className="text-sm text-secondary">
                      Looks for retirement accounts, bank accounts, life insurance, and debts in 10
                      years of IRS records, the estate&apos;s Equifax credit report, and 100+ other
                      sources. The search starts once you share your legal authority.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">$2,500</span>
                    <span className="text-xs text-secondary line-through">$3,000</span>
                    <span className="text-xs text-secondary">when you buy it now</span>
                  </div>
                  <div>
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => navigate(POST_LOSS_ROUTES.FIND_ASSETS)}
                    >
                      Unlock your search
                    </Button>
                  </div>
                </div>
              </div>
            ) : hasMinTier(tier, "premium") && hasAuthorityDocument ? (
              <div className="flex gap-4 bg-white p-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-white text-sm font-medium leading-none">
                  {4 + stepOffset}
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold">Elayne Asset Search</p>
                    <p className="text-sm text-secondary">
                      Looks for retirement accounts, bank accounts, life insurance, and debts across
                      100+ sources. It runs once your legal authority is added.
                    </p>
                  </div>
                  <div>
                    <Button
                      variant="brand"
                      size="md"
                      onClick={() => navigate(POST_LOSS_ROUTES.FIND_ASSETS)}
                    >
                      Start asset search
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[300px] shrink-0">{sidebar}</div>

      <UploadDocumentModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={() => {
          setHasAuthorityDocument();
          queryClient.invalidateQueries({ queryKey: ["userEstateRole"] });
          queryClient.invalidateQueries({ queryKey: ["hasAuthorityDocument", estateId] });
        }}
        estateId={estateId}
        documentType={getAuthorityDocumentType(hasWill)}
        size="lg"
      />
    </div>
  );
};
